import {
  LayoutDashboard,
  BookOpen,
  FlaskConical,
  Library,
  GraduationCap,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronDown,
  MessageCircle,
  FolderTree,
  Target,
  Users,
  ShieldCheck,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';

import {
  requestSsoExchangeCode,
  buildSsoRedirectUrl,
  type SwitchableModule,
} from '@aia/platform-core';
import { AppSwitcher } from '@aia/ui-components';

import { IdleTimeoutWarning } from '@/components/IdleTimeoutWarning';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { api } from '@/lib/api';
import { useAuth, type ProgressPulseRole } from '@/lib/auth';
import { cn } from '@/lib/utils';

const APP_URLS: Partial<Record<SwitchableModule, string | undefined>> = {
  pilot: import.meta.env.VITE_PILOT_URL,
  progress: import.meta.env.VITE_PROGRESS_URL,
  insight: import.meta.env.VITE_INSIGHT_URL,
  admin: import.meta.env.VITE_ADMIN_URL,
};

async function switchToApp(target: SwitchableModule): Promise<void> {
  const base = APP_URLS[target];
  if (!base) return;
  const code = await requestSsoExchangeCode(api, target);
  window.location.href = buildSsoRedirectUrl(base, code);
}

interface NavItem {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
}

const NAV_BY_ROLE: Record<ProgressPulseRole, NavItem[]> = {
  Instructor: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Classrooms', href: '/classrooms', icon: GraduationCap },
    { name: 'Dictionaries', href: '/dictionaries', icon: Library },
    { name: 'Probes', href: '/probes', icon: FlaskConical },
  ],
  Learner: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'My Classes', href: '/my-classes', icon: BookOpen },
    { name: 'Results', href: '/results', icon: BarChart3 },
    { name: 'Study Assistant', href: '/chat', icon: MessageCircle },
  ],
  OrgAdmin: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Classrooms', href: '/classrooms', icon: GraduationCap },
    { name: 'Dictionaries', href: '/dictionaries', icon: Library },
    { name: 'Probes', href: '/probes', icon: FlaskConical },
    { name: 'Instructors', href: '/instructors', icon: Users },
    { name: 'Taxonomies', href: '/taxonomies', icon: FolderTree },
    { name: 'Settings', href: '/settings', icon: Settings },
  ],
  UnitAdmin: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Classrooms', href: '/classrooms', icon: GraduationCap },
    { name: 'Dictionaries', href: '/dictionaries', icon: Library },
    { name: 'Probes', href: '/probes', icon: FlaskConical },
    { name: 'Instructors', href: '/instructors', icon: Users },
    { name: 'Taxonomies', href: '/taxonomies', icon: FolderTree },
    { name: 'Settings', href: '/settings', icon: Settings },
  ],
  DataSteward: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Classrooms', href: '/classrooms', icon: GraduationCap },
    { name: 'Dictionaries', href: '/dictionaries', icon: Library },
    { name: 'Probes', href: '/probes', icon: FlaskConical },
    { name: 'Taxonomies', href: '/taxonomies', icon: FolderTree },
  ],
  PlatformAdmin: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Classrooms', href: '/classrooms', icon: GraduationCap },
    { name: 'Dictionaries', href: '/dictionaries', icon: Library },
    { name: 'Probes', href: '/probes', icon: FlaskConical },
    { name: 'Taxonomies', href: '/taxonomies', icon: FolderTree },
    { name: 'Goal Config', href: '/goal-config', icon: Target },
    { name: 'Settings', href: '/settings', icon: Settings },
  ],
};

function getNavItems(role?: ProgressPulseRole): NavItem[] {
  if (!role) return NAV_BY_ROLE['Learner'];
  return NAV_BY_ROLE[role];
}

const ROLE_DISPLAY: Record<ProgressPulseRole, string> = {
  PlatformAdmin: 'Platform Admin',
  OrgAdmin: 'Administrator',
  UnitAdmin: 'Unit Admin',
  DataSteward: 'Data Steward',
  Instructor: 'Instructor',
  Learner: 'Learner',
};

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const breadcrumbs = useBreadcrumbs();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setSidebarOpen(false);
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  const navItems = getNavItems(user?.role);
  // A19: Settings (org + academic-year config) is admin-only. Keep the profile
  // menu link in sync with the sidebar so non-admins don't hit an Access Denied
  // dead-end.
  const canAccessSettings =
    !!user &&
    (['PlatformAdmin', 'OrgAdmin', 'UnitAdmin'] as ProgressPulseRole[]).includes(user.role);
  // A7: MFA is a staff concern; learners authenticate with email OTP and never
  // enroll a second factor, so hide the Security entry from them.
  const canAccessSecurity = !!user && user.role !== 'Learner';

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const parts = user.name.split(' ');
    if (parts.length >= 2 && parts[0] && parts[parts.length - 1]) {
      return `${parts[0][0]}${(parts[parts.length - 1] ?? '')[0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* C24: kiosk idle guard — warn and sign out after inactivity so a shared
          classroom device doesn't leave one student's session open for the next. */}
      <IdleTimeoutWarning onTimeout={handleLogout} onSwitchUser={handleLogout} />

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:p-2 focus:bg-white focus:text-black focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          role="presentation"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSidebarOpen(false);
          }}
        />
      )}

      {/* Profile menu backdrop */}
      {profileMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          role="presentation"
          onClick={() => setProfileMenuOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setProfileMenuOpen(false);
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r transform transition-transform duration-200 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        {/* AinA wordmark over the brand chart art. The product name lives on
            the ProgressPulse mark in the top bar, so it is not repeated here. */}
        <div className="relative min-h-28 overflow-hidden border-b">
          <img
            src="/brand/progresspulse-logo.png"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-white/85" aria-hidden="true" />
          <div className="relative flex min-h-28 items-center justify-center px-3 py-4">
            <Link
              to="/dashboard"
              className="flex w-full items-center justify-center"
              aria-label="Academics In Action home"
            >
              <img
                src="/brand/aina-logo.png"
                alt="Academics In Action — Where Learning Becomes Evidence"
                className="w-full max-w-[13rem] object-contain"
              />
            </Link>
            <button
              className="absolute right-2 top-2 lg:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md hover:bg-white/80 focus:outline-none focus:ring-2 focus:ring-[color:var(--aia-blue)]"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close navigation menu"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Nav links */}
        <nav className="mt-4 px-2" aria-label="Sidebar">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive(item.href)
                      ? 'bg-[color:var(--aia-gold)]/10 text-[color:var(--aia-navy)]'
                      : 'text-gray-600 hover:bg-gray-100',
                  )}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon
                    className={cn(
                      'h-5 w-5',
                      isActive(item.href) ? 'text-[color:var(--aia-gold)]' : 'text-gray-500',
                    )}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User menu at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
          <div className="relative">
            <button
              className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              aria-expanded={profileMenuOpen}
              aria-haspopup="true"
              aria-label="User profile menu"
              data-testid="user-menu"
            >
              <div className="w-8 h-8 rounded-full bg-[color:var(--aia-blue)] flex items-center justify-center text-white text-sm font-medium">
                {getUserInitials()}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-medium truncate">{user?.name || 'User'}</div>
                <div className="text-xs text-gray-500">
                  {user?.role ? ROLE_DISPLAY[user.role] : ''}
                </div>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-gray-500 transition-transform',
                  profileMenuOpen && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </button>

            {/* Dropdown */}
            {profileMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border rounded-lg shadow-lg py-1 z-50">
                <div className="px-3 py-2 border-b">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                </div>
                {canAccessSettings && (
                  <button
                    onClick={() => {
                      setProfileMenuOpen(false);
                      navigate('/settings');
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-gray-100 transition-colors"
                    aria-label="Settings"
                    data-testid="profile-button"
                  >
                    <Settings className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    Settings
                  </button>
                )}
                {canAccessSecurity && (
                  <button
                    onClick={() => {
                      setProfileMenuOpen(false);
                      navigate('/security');
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-gray-100 transition-colors"
                    aria-label="Security"
                    data-testid="profile-security-button"
                  >
                    <ShieldCheck className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    Security
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50 transition-colors"
                  aria-label="Log out"
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="min-w-0 lg:pl-64">
        {/* Top bar */}
        <header className="flex h-14 min-h-[3.5rem] items-center justify-between gap-2 border-b bg-white px-3 sm:h-16 sm:px-4">
          <div className="flex min-w-0 flex-1 items-center">
            <button
              className="lg:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[color:var(--aia-blue)]"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
            >
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <img
            src="/brand/progresspulse-wordmark.png"
            alt="ProgressPulse"
            className="h-6 w-auto shrink-0 object-contain sm:h-8"
          />

          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 sm:gap-4">
            <AppSwitcher
              currentModule="progress"
              enabledModules={user?.enabledModules ?? []}
              isPlatformAdmin={user?.isPlatformAdmin}
              appUrls={APP_URLS}
              onSwitch={switchToApp}
            />
            <button
              onClick={handleLogout}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-md px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
              aria-label="Log out"
              data-testid="header-logout-button"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Log Out</span>
            </button>
          </div>
        </header>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 1 && (
          <div className="px-4 lg:px-6 pt-3 pb-0 bg-white border-b" data-print-hide>
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-1.5 text-sm text-gray-500">
                {breadcrumbs.map((crumb, idx) => (
                  <li key={crumb.label + idx} className="flex items-center gap-1.5">
                    {idx > 0 && <span aria-hidden="true">/</span>}
                    {crumb.href ? (
                      <Link
                        to={crumb.href}
                        className="hover:text-[color:var(--aia-blue)] transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-foreground font-medium" aria-current="page">
                        {crumb.label}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        )}

        {/* Page content */}
        <main
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
          className="min-h-[calc(100vh-8rem)] min-w-0 overflow-x-hidden p-3 outline-none sm:p-4 lg:p-6"
        >
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="py-4 px-6 border-t bg-white text-center text-sm text-gray-500">
          &copy; {new Date().getFullYear()} Academics in Action LLC. All rights reserved.
        </footer>
      </div>
    </div>
  );
}
